import { useRef, useState, useEffect } from "preact/hooks";
import { forwardRef } from "preact/compat";
import { isIos } from "@/lib/web";
import { doLog } from "@/lib/globals";

const POLLING_INTERVAL = 250;
const POLLING_TIMEOUT = 3000;

const ChatScaffold = forwardRef(({ header, body, footer, onViewportChange }, ref) => {
  // MARK: useState
  const [isPolling, setIsPolling] = useState(false);
  const [vv, setVv] = useState({
    offsetTop: 0,
    offsetLeft: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // MARK: useRef
  const headerRef = useRef();
  const footerRef = useRef();
  const bodyRef = useRef();
  const rootRef = useRef();
  const messagesEndRef = useRef();
  const pollingRef = useRef(null);
  const lastDimensionsRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight,
    offsetTop: 0,
    offsetLeft: 0,
  });

  // MARK: Update Layout
  const updateLayout = () => {
    if (doLog()) console.log("updateLayout()");
    const viewport = window.visualViewport || window;
    let offsetTop = 0;
    let offsetLeft = 0;
    if (isIos) {
      // iOS Safari: use visualViewport for keyboard handling
      offsetTop = viewport.offsetTop || 0;
      offsetLeft = viewport.offsetLeft || 0;
    }
    const width = viewport.width || window.innerWidth;
    const height = viewport.height || window.innerHeight;

    // Use requestAnimationFrame for visual updates
    requestAnimationFrame(() => {
      const hh = headerRef.current.getBoundingClientRect().height;
      const fh = footerRef.current.getBoundingClientRect().height;
      // console.log("updateLayout() with values:", offsetTop, height, hh, fh);
      bodyRef.current.style.height = `${height - hh - fh}px`;
      setVv({ offsetTop, offsetLeft, width, height });
      onViewportChange?.();
    });
  };

  // MARK: Expect Change
  const expectViewportChange = () => {
    if (doLog()) console.log("expectViewportChange() - isPolling:", isPolling);
    if (isPolling) return; // Ignore if already polling

    const startTime = Date.now();
    const viewport = window.visualViewport || window;
    let intervalId = null;

    // Store initial dimensions
    lastDimensionsRef.current = {
      width: viewport.width || window.innerWidth,
      height: viewport.height || window.innerHeight,
      offsetTop: viewport.offsetTop || 0,
      offsetLeft: viewport.offsetLeft || 0,
    };

    const stopPolling = () => {
      if (intervalId) {
        if (doLog()) console.log("Stopping interval:", intervalId);
        clearInterval(intervalId);
        intervalId = null;
      }
      setIsPolling(false);
    };

    const checkViewport = () => {
      // if (doLog()) console.log("checkViewport() called");
      const currentTime = Date.now();
      // const diff = currentTime - startTime;
      // if (doLog()) console.log("expectViewportChange() - diff:", diff);

      if (currentTime - startTime >= POLLING_TIMEOUT) {
        // Stop polling after 3000ms
        if (doLog()) console.log("expectViewportChange() - polling timeout");
        stopPolling();
        return;
      }

      const currentDimensions = {
        width: viewport.width || window.innerWidth,
        height: viewport.height || window.innerHeight,
        offsetTop: viewport.offsetTop || 0,
        offsetLeft: viewport.offsetLeft || 0,
      };

      // Check if dimensions have changed
      if (
        currentDimensions.width !== lastDimensionsRef.current.width ||
        currentDimensions.height !== lastDimensionsRef.current.height ||
        currentDimensions.offsetTop !== lastDimensionsRef.current.offsetTop ||
        currentDimensions.offsetLeft !== lastDimensionsRef.current.offsetLeft
      ) {
        if (doLog())
          console.log(
            "expectViewportChange() - dimensions changed",
            currentDimensions,
            lastDimensionsRef.current
          );
        stopPolling();
        updateLayout();
        return;
      }
    };

    // Start polling
    setIsPolling(true);
    if (doLog()) console.log("expectViewportChange() - polling started");
    if (doLog())
      console.log("Setting up interval with POLLING_INTERVAL:", POLLING_INTERVAL);

    // Set up new interval
    intervalId = setInterval(checkViewport, POLLING_INTERVAL);
    if (doLog()) console.log("Interval ID:", intervalId);

    // Call checkViewport immediately
    checkViewport();

    // Return cleanup function
    return () => {
      stopPolling();
    };
  };

  // MARK: Root Ref Setup
  useEffect(() => {
    if (ref) {
      if (doLog()) console.log("ref is valid");
      ref.current = {
        expectViewportChange,
        scrollToBottom,
      };
    } else {
      if (doLog()) console.log("ref is null");
    }
  }, [ref]);

  // MARK: Cleanup
  useEffect(() => {
    return () => {
      setIsPolling(false);
    };
  }, []);

  // MARK: Debounce Events
  useEffect(() => {
    let timer;
    const debounce = () => {
      if (isPolling) {
        if (doLog()) console.log("debounce ignored - polling is active");
        return;
      }
      if (doLog()) console.log("debounce()");
      clearTimeout(timer);
      timer = setTimeout(updateLayout, 500); // 500ms results in a smooth-ish transition
    };

    // Only prevent scroll on the outer container
    const preventScroll = (e) => {
      if (window.visualViewport && e.target === document.body) {
        e.preventDefault();
      }
    };

    updateLayout();
    window.visualViewport?.addEventListener("resize", debounce);
    window.visualViewport?.addEventListener("scroll", preventScroll);
    window.addEventListener("resize", debounce);
    window.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      window.visualViewport?.removeEventListener("resize", debounce);
      window.visualViewport?.removeEventListener("scroll", preventScroll);
      window.removeEventListener("resize", debounce);
      window.removeEventListener("touchmove", preventScroll);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isPolling]);

  // ====================================================================================|
  //  MARK: Scroll to Bottom

  let scrollToBottomRunId = 0;

  const scrollToBottom = () => {
    // if (doLog()) console.log("scrollToBottom called");

    const runId = ++scrollToBottomRunId;
    let first = true;

    const scroll = () => {
      if (runId !== scrollToBottomRunId) {
        // if (doLog()) console.log("scrollToBottom: runId mismatch, aborting");
        return;
      }

      const el = messagesEndRef?.current;
      if (!el) {
        if (doLog()) console.log("scrollToBottom: Missing messagesEndRef");
        return;
      }

      // if (doLog()) console.log("scrollToBottom: attempting scroll");

      // Force a reflow to ensure the element is properly positioned
      el.getBoundingClientRect();

      // Use smooth scroll for first attempt, auto for subsequent
      el.scrollIntoView({
        behavior: first ? "smooth" : "auto",
        block: "end",
        inline: "nearest",
      });

      first = false;

      // Double check scroll position after a short delay
      requestAnimationFrame(() => {
        if (runId !== scrollToBottomRunId) return;

        const rect = el.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        // if (doLog())
        //   console.log("scrollToBottom: scroll verification", {
        //     isVisible,
        //     top: rect.top,
        //     bottom: rect.bottom,
        //     windowHeight: window.innerHeight,
        //   });

        if (!isVisible) {
          if (doLog())
            console.log(
              "scrollToBottom: scroll verification failed, retrying with auto behavior"
            );
          el.scrollIntoView({ behavior: "auto", block: "end", inline: "nearest" });
        }
      });
    };

    // Immediate attempt
    requestAnimationFrame(scroll);

    // Additional attempts with increasing delays
    [100, 250, 500, 1000].forEach((delay) => {
      setTimeout(() => {
        // if (doLog()) console.log(`scrollToBottom: delayed attempt after ${delay}ms`);
        scroll();
      }, delay);
    });
  };

  // ====================================================================================|
  //  MARK: Resize Observers

  /* 
    Add ResizeObserver to handle dynamic content changes.

    The code creates a ResizeObserver that watches bodyRef.current, which is a div element that contains the message list.

    The ResizeObserver will trigger whenever this container's dimensions change, which could happen when:
    - New messages are added to the list
    - The content inside the container changes size
    - The container's parent elements change size
  */
  useEffect(() => {
    const container = bodyRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
      scrollToBottom();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Add ResizeObserver for the footer
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
      scrollToBottom();
    });

    resizeObserver.observe(footer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // ====================================================================================|
  // MARK: Render
  return (
    <div
      ref={rootRef}
      className="w-full max-w-[500px] mx-auto"
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: "50%", // center horizontally
        transform: isIos
          ? `translate(calc(-50% + ${vv.offsetLeft}px), ${vv.offsetTop}px)`
          : "translateX(-50%)",
        width: `${vv.width}px`,
        height: `${vv.height}px`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        touchAction: "none",
        overscrollBehavior: "none",
        WebkitOverflowScrolling: "none",
        overscrollBehaviorY: "none",
        overscrollBehaviorX: "none",
        zIndex: 9999,
      }}
    >
      <header ref={headerRef} style={{ flexShrink: 0, position: "relative" }}>
        {header}
      </header>
      <main
        ref={bodyRef}
        style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          position: "relative",
          touchAction: "pan-y",
        }}
      >
        {body}
        <div ref={messagesEndRef} />
      </main>
      <footer
        ref={footerRef}
        style={{ flexShrink: 0, position: "relative" }}
        className="mx-2 mb-2 mt-1"
      >
        {footer}
      </footer>
    </div>
  );
});

export default ChatScaffold;
